/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ClipboardCheck, ChevronDown, ChevronUp, Check, AlertCircle } from 'lucide-react';

/**
 * Renders all of a subscriber's currently-due assigned tracking forms,
 * grouped by form_type (daily | event | one_time). Falls back silently when
 * there are no assignments — caller still shows the legacy default form.
 *
 * Source of truth: GET /api/tracking/active-assignments/me
 * Submit:           POST /api/tracking/responses
 */
export default function AssignedTrackingForms({ onSubmitted }) {
  const [data, setData] = useState({ assignments: [], default_template: null, today: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tracking/active-assignments/me`);
      setData(r.data || { assignments: [] });
    } catch {
      // silent — subscriber should still see fallback form
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter out the default-template assignment if present — the existing
  // hardcoded Parkinson form below already covers that one through /api/checkins.
  const nonDefaultAssignments = (data.assignments || []).filter(
    (a) => a.template?.id !== 'tpl_default_daily_observation',
  );

  if (loading || nonDefaultAssignments.length === 0) return null;

  const grouped = {
    daily:    nonDefaultAssignments.filter((a) => a.template?.form_type === 'daily'),
    event:    nonDefaultAssignments.filter((a) => a.template?.form_type === 'event'),
    one_time: nonDefaultAssignments.filter((a) => a.template?.form_type === 'one_time'),
  };

  return (
    <section className="mb-14 space-y-8" data-testid="assigned-tracking-forms">
      <FormGroup
        title="Today's Daily Tracking"
        items={grouped.daily}
        onSubmitted={() => { load(); onSubmitted?.(); }}
        defaultOpen={grouped.daily.length === 1 && grouped.event.length === 0 && grouped.one_time.length === 0}
        testid="group-daily"
      />
      <FormGroup
        title="Event Monitoring"
        items={grouped.event}
        onSubmitted={() => { load(); onSubmitted?.(); }}
        testid="group-event"
      />
      <FormGroup
        title="One-Time Assessments"
        items={grouped.one_time}
        onSubmitted={() => { load(); onSubmitted?.(); }}
        testid="group-one-time"
      />
    </section>
  );
}

function FormGroup({ title, items, onSubmitted, defaultOpen, testid }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
        {title}
      </p>
      <div className="space-y-3">
        {items.map((a) => (
          <AssignmentCard key={a.id} assignment={a} defaultOpen={defaultOpen} onSubmitted={onSubmitted} />
        ))}
      </div>
    </div>
  );
}

function AssignmentCard({ assignment, defaultOpen, onSubmitted }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const tpl = assignment.template || {};
  const submittedToday = !!assignment.submitted_today;

  return (
    <div className="rounded-lg border border-bio-border/60 bg-bio-surface-raised/30" data-testid={`assignment-card-${assignment.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-center justify-between gap-3 px-4 py-3"
        data-testid={`toggle-assignment-${assignment.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ClipboardCheck className="w-4 h-4 text-bio-accent shrink-0" />
          <div className="min-w-0">
            <div className="text-sm text-bio-primary font-['Work_Sans'] truncate">{assignment.assignment_title || tpl.name}</div>
            <div className="text-[11px] text-bio-faint mt-0.5">
              {tpl.form_type}
              {assignment.required && <span className="text-rose-300/80 ml-1">· required</span>}
              {submittedToday && <span className="text-emerald-400/90 ml-1">· submitted today</span>}
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-bio-subtle" /> : <ChevronDown className="w-4 h-4 text-bio-subtle" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <DynamicForm
            assignment={assignment}
            template={tpl}
            onSubmitted={onSubmitted}
          />
        </div>
      )}
    </div>
  );
}

function DynamicForm({ assignment, template, onSubmitted }) {
  const initial = {};
  (template.questions || []).forEach((q) => {
    if (q.type === 'slider' || q.type === 'scale_1_10' || q.type === 'number') {
      const def = q.min != null ? q.min : 0;
      initial[q.key] = def;
    } else if (q.type === 'yes_no') {
      initial[q.key] = '';
    } else if (q.type === 'multi_choice') {
      initial[q.key] = [];
    } else {
      initial[q.key] = '';
    }
  });
  const [answers, setAnswers] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [flagsAfter, setFlagsAfter] = useState([]);

  const set = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    // basic required validation
    for (const q of (template.questions || [])) {
      if (q.required) {
        const v = answers[q.key];
        if (v === '' || v == null || (Array.isArray(v) && v.length === 0)) {
          toast.error(`Required: ${q.label || q.key}`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/tracking/responses`, {
        assignment_id: assignment.id,
        template_id: template.id,
        answers,
      });
      setReflection(r.data?.subscriber_reflection || 'Your observation has been saved.');
      setFlagsAfter(r.data?.flags || []);
      toast.success('Observation saved');
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (reflection) {
    return (
      <div className="space-y-3" data-testid={`reflection-${assignment.id}`}>
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
          <div className="flex items-center gap-2 text-emerald-300 text-sm mb-1">
            <Check className="w-4 h-4" /> Saved
          </div>
          <p className="text-sm text-bio-body leading-relaxed">{reflection}</p>
        </div>
        {flagsAfter.length > 0 && (
          <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-4 text-sm">
            <div className="flex items-center gap-2 text-rose-300 mb-1">
              <AlertCircle className="w-4 h-4" /> Your care team has been notified
            </div>
            <p className="text-xs text-bio-subtle">
              Today's observation triggered {flagsAfter.length} signal{flagsAfter.length > 1 ? 's' : ''} that your care team will review.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-5" data-testid={`dynamic-form-${assignment.id}`}>
      {(template.questions || []).map((q) => (
        <QuestionField key={q.key} q={q} value={answers[q.key]} onChange={(v) => set(q.key, v)} testid={`q-${q.key}`} />
      ))}
      <Button type="submit" disabled={saving}
        className="w-full py-2.5 bg-bio-accent/30 border border-bio-accent/40 text-bio-primary hover:bg-bio-accent/40"
        data-testid={`submit-${assignment.id}`}>
        {saving ? 'Saving…' : 'Save Observation'}
      </Button>
    </form>
  );
}

function QuestionField({ q, value, onChange, testid }) {
  const lbl = (
    <div className="flex justify-between text-sm mb-1.5">
      <span className="text-bio-body font-['Work_Sans']">{q.label || q.key}{q.required && <span className="text-rose-300/80 ml-1">*</span>}</span>
      {(q.type === 'slider' || q.type === 'scale_1_10') && (
        <span className="text-bio-subtle font-['Work_Sans']">{value ?? 0}{q.unit ? q.unit : ''}</span>
      )}
    </div>
  );
  switch (q.type) {
    case 'slider':
    case 'scale_1_10': {
      const max = q.max ?? (q.type === 'scale_1_10' ? 10 : 100);
      const min = q.min ?? 0;
      return (
        <div data-testid={testid}>
          {lbl}
          <Slider value={[Number(value ?? min)]} onValueChange={([v]) => onChange(v)}
            min={min} max={max} step={1}
            className="[&_[role=slider]]:bg-bio-accent [&_[role=slider]]:border-0" />
          {q.help_text && <p className="text-[11px] text-bio-faint mt-1">{q.help_text}</p>}
        </div>
      );
    }
    case 'number':
      return (
        <div data-testid={testid}>
          {lbl}
          <Input type="number" min={q.min} max={q.max} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
          {q.help_text && <p className="text-[11px] text-bio-faint mt-1">{q.help_text}</p>}
        </div>
      );
    case 'text':
      return (
        <div data-testid={testid}>
          {lbl}
          <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={q.help_text || ''} />
        </div>
      );
    case 'textarea':
      return (
        <div data-testid={testid}>
          {lbl}
          <Textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={q.help_text || ''} />
        </div>
      );
    case 'date':
      return (<div data-testid={testid}>{lbl}<Input type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></div>);
    case 'time':
      return (<div data-testid={testid}>{lbl}<Input type="time" value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></div>);
    case 'yes_no':
      return (
        <div data-testid={testid}>
          {lbl}
          <div className="flex gap-2">
            {['yes', 'no'].map((opt) => (
              <button type="button" key={opt}
                onClick={() => onChange(opt)}
                className={`flex-1 py-2 rounded border text-sm transition-colors ${
                  value === opt
                    ? 'bg-bio-accent/30 border-bio-accent/50 text-bio-primary'
                    : 'border-bio-border text-bio-subtle hover:bg-bio-surface-raised/50'
                }`}
                data-testid={`${testid}-${opt}`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    case 'single_choice':
      return (
        <div data-testid={testid}>
          {lbl}
          <div className="grid gap-1.5">
            {(q.options || []).map((opt) => (
              <button type="button" key={opt}
                onClick={() => onChange(opt)}
                className={`text-left px-3 py-2 rounded border text-sm transition-colors ${
                  value === opt
                    ? 'bg-bio-accent/30 border-bio-accent/50 text-bio-primary'
                    : 'border-bio-border text-bio-subtle hover:bg-bio-surface-raised/50'
                }`}
                data-testid={`${testid}-${opt}`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    case 'multi_choice':
      return (
        <div data-testid={testid}>
          {lbl}
          <div className="grid gap-1.5">
            {(q.options || []).map((opt) => {
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.includes(opt);
              return (
                <button type="button" key={opt}
                  onClick={() => onChange(checked ? arr.filter((x) => x !== opt) : [...arr, opt])}
                  className={`text-left px-3 py-2 rounded border text-sm transition-colors ${
                    checked
                      ? 'bg-bio-accent/30 border-bio-accent/50 text-bio-primary'
                      : 'border-bio-border text-bio-subtle hover:bg-bio-surface-raised/50'
                  }`}
                  data-testid={`${testid}-${opt}`}>
                  {checked ? '✓ ' : ''}{opt}
                </button>
              );
            })}
          </div>
        </div>
      );
    default:
      return (
        <div data-testid={testid}>
          {lbl}
          <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
        </div>
      );
  }
}
